import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Fire, Medal, PencilSimple, PlayCircle, Target, Trophy } from '@phosphor-icons/react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { useCourse } from '../lib/course';
import { useAsync } from '../lib/useAsync';
import { formatDuration, pct } from '../lib/format';
import {
  Card,
  ErrorState,
  LoadingState,
  Pill,
  ProgressBar,
  ProgressRing,
  SectionHeading,
  StatCard,
} from '../components/ui';

export function Dashboard() {
  const { user } = useAuth();
  const { coursePath } = useCourse();
  const state = useAsync(() => Promise.all([api.dashboard(), api.program()]), []);
  const [goalOverride, setGoalOverride] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(5);
  const [savingGoal, setSavingGoal] = useState(false);

  if (state.loading) return <LoadingState />;
  if (state.error || !state.data) return <ErrorState message={state.error ?? 'Failed to load'} onRetry={state.reload} />;

  const [{ summary, badges, continueLesson }, program] = state.data;
  const goal = goalOverride ?? summary?.weeklyGoal ?? 5;
  const done = summary?.weeklyCompleted ?? 0;
  const goalPct = goal === 0 ? 0 : (done / goal) * 100;

  async function saveGoal() {
    const n = Math.min(50, Math.max(1, Math.round(draft) || 1));
    setSavingGoal(true);
    try {
      await api.setStudyGoal(n);
      setGoalOverride(n);
      setEditing(false);
    } catch {
      /* keep the editor open if the save fails */
    } finally {
      setSavingGoal(false);
    }
  }
  const earned = new Set(summary?.earnedBadgeIds ?? []);
  const completedModules = program.modules.filter((m) => m.state === 'completed').length;
  const classCompletionPct = program.modules.length ? (completedModules / program.modules.length) * 100 : 0;
  // The class (and its section) the "continue" lesson belongs to — students think
  // in classes, not lesson filenames like "Session plan".
  const continueModule = continueLesson ? program.modules.find((m) => m.id === continueLesson.moduleId) ?? null : null;
  const continueSection = continueModule?.sectionId
    ? program.sections.find((s) => s.id === continueModule.sectionId) ?? null
    : null;
  const firstName = user?.name.split(' ')[0] ?? 'there';

  return (
    <div className="space-y-6">
      <SectionHeading
        eyebrow="My Progress"
        title={`Welcome back, ${firstName}`}
        description="Keep your streak going and hit your study goal."
      />

      {/* Top stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={Fire}
          tone="amber"
          label="Current streak"
          value={summary?.streakDays ? `${summary.streakDays} days` : 'Start today'}
          hint={summary?.streakDays ? 'Daily learning streak' : 'Complete a class to begin'}
        />
        <StatCard icon={Target} tone="teal" label="Course progress" value={pct(classCompletionPct)} hint="of the course" />
        <StatCard
          icon={PlayCircle}
          tone="green"
          label="Classes completed"
          value={completedModules}
          hint={`of ${program.modules.length}`}
        />
        <StatCard icon={Trophy} tone="violet" label="Badges" value={earned.size} hint={`of ${badges.length} earned`} />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Weekly goal ring */}
        <Card className="flex flex-col items-center justify-center p-6">
          <div className="mb-3 flex items-center gap-1.5">
            <span className="label-mono">Study goal</span>
            {!editing && (
              <button
                type="button"
                className="btn-ghost p-1 text-slate-400 hover:text-brand-teal"
                aria-label="Edit study goal"
                onClick={() => {
                  setDraft(goal);
                  setEditing(true);
                }}
              >
                <PencilSimple size={14} weight="bold" />
              </button>
            )}
          </div>
          <ProgressRing value={goalPct}>
            <span className="text-3xl font-extrabold text-brand-navy">{done}</span>
            <span className="text-xs text-slate-500">of {goal} this week</span>
          </ProgressRing>
          {editing ? (
            <div className="mt-4 flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={50}
                value={draft}
                onChange={(e) => setDraft(Number(e.target.value))}
                className="input w-20 text-center"
                aria-label="Lessons per week"
              />
              <span className="text-xs text-slate-500">/ week</span>
              <button type="button" className="btn-primary px-3 py-1.5 text-sm" disabled={savingGoal} onClick={saveGoal}>
                <Check size={16} weight="bold" /> {savingGoal ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="btn-ghost px-2 py-1.5 text-sm" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </div>
          ) : (
            <p className="mt-4 text-center text-sm text-slate-500">
              {done >= goal
                ? '🎉 Goal reached — nice work!'
                : `${Math.max(0, goal - done)} to go · ${goal} class resources / week`}
            </p>
          )}
        </Card>

        {/* Continue learning */}
        <Card className="flex flex-col justify-between p-6 lg:col-span-2">
          <div>
            <span className="label-mono">Continue learning</span>
            {continueLesson ? (
              <div className="mt-3">
                <h3 className="text-lg font-bold">{continueModule?.title ?? continueLesson.title}</h3>
                <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                  {continueSection && <span>{continueSection.title}</span>}
                  <Pill tone="teal">{continueLesson.type}</Pill>
                  {continueLesson.durationSec > 0 && <span>{formatDuration(continueLesson.durationSec)}</span>}
                </p>
              </div>
            ) : (
              <p className="mt-3 text-slate-500">You're all caught up. New content drips in as it's recorded.</p>
            )}
          </div>
          {continueLesson && (
            <Link to={coursePath(`classes/${continueLesson.moduleId}`)} className="btn-primary mt-6 self-start">
              Resume class <ArrowRight size={18} weight="bold" />
            </Link>
          )}
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Topic mastery */}
        <Card className="p-6 lg:col-span-2">
          <h3 className="mb-4 font-bold">Topic mastery</h3>
          {summary && summary.topicMastery.length > 0 ? (
            <div className="space-y-4">
              {summary.topicMastery.map((t) => (
                <div key={t.topic}>
                  <div className="mb-1 flex justify-between text-sm">
                    <span className="font-medium text-slate-700">{t.topic}</span>
                    <span className="font-semibold text-slate-500">{pct(t.mastery)}</span>
                  </div>
                  <ProgressBar value={t.mastery} />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">Mastery appears as you complete lessons and quizzes.</p>
          )}
        </Card>

        {/* Badges */}
        <Card className="p-6">
          <h3 className="mb-4 font-bold">Badges</h3>
          <ul className="space-y-3">
            {badges.map((b) => {
              const has = earned.has(b.id);
              return (
                <li key={b.id} className="flex items-center gap-3">
                  <span
                    className={
                      'flex h-9 w-9 items-center justify-center rounded-lg ' +
                      (has ? 'bg-violet-50 text-violet-600' : 'bg-slate-100 text-slate-300')
                    }
                  >
                    <Medal size={20} weight={has ? 'fill' : 'regular'} />
                  </span>
                  <div className="min-w-0">
                    <div className={'text-sm font-semibold ' + (has ? 'text-brand-navy' : 'text-slate-400')}>
                      {b.name}
                    </div>
                    <div className="truncate text-xs text-slate-400">{b.description}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>
    </div>
  );
}
